import * as React from "react";
import { Server, Globe, Sliders, Cpu, ChevronDown, ChevronRight } from "react-feather";
import { Radio } from "antd";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  selectProcessedGvcs,
  selectProcessedDomains,
  selectProcessedQuotas,
  selectUsageMetrics,
  selectIsUsageLoading,
  selectUsageDateRange,
  selectIsDomainsLoading,
  selectIsAgentsLoading,
  selectIsWorkloadsLoading,
  selectIsQuotasLoading,
  fetchUsageMetrics,
  setUsageDateRange,
  UsageDateRange,
} from "../../store/healthSlice";
import { selectOrg } from "../../store/contextSlice";
import { HealthSummaryCard, HealthIssueCard, GvcHealthCard, AgentHealthCard, getAgentStatus, MetricSummaryCard, AlternatingMetricCard, AlternatingObservabilityCard, DomainHealthCard, SectionLoader } from "./components";
import {
  getWorkloadHealthStatus,
  getDomainHealthStatus,
  getQuotaHealthStatus,
  collectAllIssues,
} from "./utils";
import "./health.css";

export const HealthOverview: React.FC = () => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);
  const agents = useAppSelector((state) => state.health.agents);
  const agentInfoMap = useAppSelector((state) => state.health.agentInfoMap);
  const domains = useAppSelector((state) => state.health.domains);

  const processedGvcs = useAppSelector(selectProcessedGvcs);
  const processedDomains = useAppSelector(selectProcessedDomains);
  const processedQuotas = useAppSelector(selectProcessedQuotas);

  // Usage metrics
  const usageMetrics = useAppSelector(selectUsageMetrics);
  const isUsageLoading = useAppSelector(selectIsUsageLoading);
  const usageDateRange = useAppSelector(selectUsageDateRange);

  // Section loading states
  const isDomainsLoading = useAppSelector(selectIsDomainsLoading);
  const isQuotasLoading = useAppSelector(selectIsQuotasLoading);
  const isAgentsLoading = useAppSelector(selectIsAgentsLoading);
  const isWorkloadsLoading = useAppSelector(selectIsWorkloadsLoading);

  // Fetch usage metrics on mount and when org or date range changes
  React.useEffect(() => {
    if (org) {
      dispatch(fetchUsageMetrics({ orgName: org, dateRange: usageDateRange }));
    }
  }, [dispatch, org, usageDateRange]);

  const handleDateRangeChange = (value: UsageDateRange) => {
    dispatch(setUsageDateRange(value));
  };

  // Track which issue card is expanded (only one at a time)
  const [expandedIssueId, setExpandedIssueId] = React.useState<string | null>(null);

  // Track which GVC card is expanded (only one at a time)
  const [expandedGvcName, setExpandedGvcName] = React.useState<string | null>(null);

  // Track how many issues to show (paginated by 6)
  const [visibleIssueCount, setVisibleIssueCount] = React.useState(6);

  // Track collapsed sections - Workload Health expanded by default, others collapsed
  const [collapsedSections, setCollapsedSections] = React.useState<Record<string, boolean>>({
    workloadHealth: false,
    domainHealth: true,
    gvcHealth: true,
    agentStatus: true,
  });

  // Toggle to show only items with issues
  const [showIssuesOnly, setShowIssuesOnly] = React.useState(false);

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Calculate health stats
  const workloadHealth = React.useMemo(() => getWorkloadHealthStatus(processedGvcs), [processedGvcs]);

  const domainHealth = React.useMemo(() => {
    const status = getDomainHealthStatus(processedDomains);
    // Add ready count (total domains - issues)
    return {
      ...status,
      ready: domains.length - status.warning - status.error,
      total: domains.length,
    };
  }, [processedDomains, domains]);

  const quotas = useAppSelector((state) => state.health.quotas);

  const quotaHealth = React.useMemo(() => {
    const status = getQuotaHealthStatus(processedQuotas);
    // ok = quotas without issues, nearLimit = warnings, exceeded = errors
    return {
      ok: quotas.length - status.warning - status.error,
      nearLimit: status.warning,
      exceeded: status.error,
      total: quotas.length,
    };
  }, [processedQuotas, quotas]);

  const agentHealth = React.useMemo(() => {
    let active = 0;
    let recentlyInactive = 0;
    let neverActive = 0;
    let checking = 0;

    for (const agent of agents) {
      const status = getAgentStatus(agent, agentInfoMap[agent.name]);
      if (status === "active") active++;
      else if (status === "recently-inactive") recentlyInactive++;
      else if (status === "checking") checking++;
      else neverActive++;
    }

    return { active, recentlyInactive, neverActive, checking, total: agents.length };
  }, [agents, agentInfoMap]);

  // Collect all issues
  const allIssues = React.useMemo(
    () => collectAllIssues(processedGvcs, processedDomains, processedQuotas),
    [processedGvcs, processedDomains, processedQuotas],
  );

  // Filter GVCs with at least one workload and sort by severity (errors first, then warnings, then healthy)
  const gvcsWithWorkloads = React.useMemo(() => {
    return processedGvcs
      .filter((gvc) => {
        const totalWorkloads =
          gvc.workloads.ready.length +
          gvc.workloads.notReady.length +
          gvc.workloads.notLatest.length +
          gvc.workloads.syncFailed.length +
          gvc.workloads.suspended.length +
          gvc.workloads.partiallySuspended.length;
        if (totalWorkloads === 0) return false;

        // When showIssuesOnly is true, only include GVCs with issues
        if (showIssuesOnly) {
          const hasIssues =
            gvc.workloads.notReady.length > 0 ||
            gvc.workloads.notLatest.length > 0 ||
            gvc.workloads.syncFailed.length > 0;
          return hasIssues;
        }

        return true;
      })
      .sort((a, b) => {
        const errorCountA = a.workloads.notReady.length + a.workloads.syncFailed.length;
        const errorCountB = b.workloads.notReady.length + b.workloads.syncFailed.length;
        const warningCountA = a.workloads.notLatest.length;
        const warningCountB = b.workloads.notLatest.length;

        // Errors first
        if (errorCountA > 0 && errorCountB === 0) return -1;
        if (errorCountA === 0 && errorCountB > 0) return 1;

        // Then warnings
        if (warningCountA > 0 && warningCountB === 0) return -1;
        if (warningCountA === 0 && warningCountB > 0) return 1;

        // Then by name
        return a.item.name.localeCompare(b.item.name);
      });
  }, [processedGvcs, showIssuesOnly]);

  // Sort agents: recently-inactive first, then active, then checking, then never-active
  const sortedAgents = React.useMemo(() => {
    return [...agents]
      .filter((agent) => {
        if (!showIssuesOnly) return true;
        const status = getAgentStatus(agent, agentInfoMap[agent.name]);
        // Only show recently-inactive and checking agents when showIssuesOnly is true
        return status === "recently-inactive" || status === "checking";
      })
      .sort((a, b) => {
        const statusA = getAgentStatus(a, agentInfoMap[a.name]);
        const statusB = getAgentStatus(b, agentInfoMap[b.name]);

        const priority = { "recently-inactive": 0, active: 1, checking: 2, "never-active": 3 };
        return priority[statusA] - priority[statusB];
      });
  }, [agents, agentInfoMap, showIssuesOnly]);

  return (
    <div className="health-page">
      <div className="health-layout">
        {/* Summary Cards - always full width */}
        <div className="health-summary">
          <div className="health-summary-grid">
            <HealthSummaryCard
              title="Workloads"
              icon={<Server size={16} />}
              variant="workload"
              data={{
                ready: workloadHealth.ready,
                warning: workloadHealth.warning,
                error: workloadHealth.error,
              }}
              isLoading={isWorkloadsLoading}
            />
            <HealthSummaryCard
              title="Domains"
              icon={<Globe size={16} />}
              variant="domain"
              data={{
                ready: domainHealth.ready,
                warning: domainHealth.warning,
                error: domainHealth.error,
              }}
              isLoading={isDomainsLoading}
            />
            <HealthSummaryCard
              title="Quotas"
              icon={<Sliders size={16} />}
              variant="quota"
              data={{
                ok: quotaHealth.ok,
                nearLimit: quotaHealth.nearLimit,
                exceeded: quotaHealth.exceeded,
              }}
              isLoading={isQuotasLoading}
            />
            <HealthSummaryCard
              title="Agents"
              icon={<Cpu size={16} />}
              variant="agent"
              data={{
                active: agentHealth.active,
                recentlyInactive: agentHealth.recentlyInactive,
                neverActive: agentHealth.neverActive,
                checking: agentHealth.checking,
              }}
              isLoading={isAgentsLoading}
            />
          </div>
        </div>

        {/* Two-column content area */}
        <div className="health-columns">
          {/* Left column: Billing, GVC Health, Domain Health */}
          <div className="health-left">
            {/* Billing */}
            <div className="health-billing">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="text-lg font-medium" style={{ color: "var(--color-default)" }}>
                  Billing
                </h2>
                <Radio.Group
                  value={usageDateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  options={[
                    { value: "thisMonth", label: "This Month" },
                    { value: "lastMonth", label: "Last Month" },
                  ]}
                />
              </div>
              <div className="health-billing-grid">
                <MetricSummaryCard
                  variant="projectedCost"
                  value={usageMetrics.projectedMonthlyCost}
                  actualSpendToDate={usageMetrics.actualSpendToDate}
                  isLoading={isUsageLoading}
                  dateRange={usageDateRange}
                />
                <MetricSummaryCard
                  variant="workloads"
                  value={usageMetrics.workloadCount}
                  isLoading={isUsageLoading}
                />
                <MetricSummaryCard
                  variant="cpu"
                  value={usageMetrics.cpuCores}
                  monthlyCost={usageMetrics.cpuMonthlyCost}
                  isLoading={isUsageLoading}
                />
                <MetricSummaryCard
                  variant="ram"
                  value={usageMetrics.ramGib}
                  monthlyCost={usageMetrics.ramMonthlyCost}
                  isLoading={isUsageLoading}
                />
                <MetricSummaryCard
                  variant="egress"
                  value={usageMetrics.egressGb}
                  monthlyCost={usageMetrics.egressMonthlyCost}
                  isLoading={isUsageLoading}
                />
                <AlternatingMetricCard
                  loadBalancerCount={usageMetrics.loadBalancerCount}
                  loadBalancerMonthlyCost={usageMetrics.loadBalancerMonthlyCost}
                  mk8sCores={usageMetrics.mk8sCores}
                  mk8sMonthlyCost={usageMetrics.mk8sMonthlyCost}
                  isLoading={isUsageLoading}
                />
                <AlternatingObservabilityCard
                  observabilityGb={usageMetrics.observabilityGb}
                  observabilityMonthlyCost={usageMetrics.observabilityMonthlyCost}
                  metricsSamplesCount={usageMetrics.metricsSamplesCount}
                  metricsSamplesMonthlyCost={usageMetrics.metricsSamplesMonthlyCost}
                  isLoading={isUsageLoading}
                />
                <MetricSummaryCard
                  variant="storage"
                  value={usageMetrics.storageGb}
                  monthlyCost={usageMetrics.storageMonthlyCost}
                  isLoading={isUsageLoading}
                />
              </div>
            </div>

            {/* GVC Health */}
            <div className="health-gvc">
              {isWorkloadsLoading ? (
                <>
                  <h2 className="text-lg font-medium mb-4" style={{ color: "var(--color-default)" }}>
                    GVC Health
                  </h2>
                  <SectionLoader count={4} className="health-gvc-grid" />
                </>
              ) : (
                gvcsWithWorkloads.length > 0 && (
                  <>
                    <button
                      onClick={() => toggleSection("gvcHealth")}
                      className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
                      style={{ color: "var(--color-default)" }}
                    >
                      {collapsedSections.gvcHealth ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                      <h2 className="text-lg font-medium">GVC Health</h2>
                      <span className="text-sm" style={{ color: "var(--color-label)" }}>
                        ({gvcsWithWorkloads.length})
                      </span>
                    </button>
                    {!collapsedSections.gvcHealth && (
                      <div className="health-gvc-grid">
                        {gvcsWithWorkloads.map((gvc) => (
                          <GvcHealthCard
                            key={gvc.item.name}
                            gvc={gvc}
                            isExpanded={expandedGvcName === gvc.item.name}
                            onToggle={() => setExpandedGvcName(expandedGvcName === gvc.item.name ? null : gvc.item.name)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )
              )}
            </div>

            {/* Domain Health */}
            <div className="health-domain">
              {isDomainsLoading ? (
                <>
                  <h2 className="text-lg font-medium mb-4" style={{ color: "var(--color-default)" }}>
                    Domain Health
                  </h2>
                  <SectionLoader count={4} className="health-domain-grid" />
                </>
              ) : (
                processedDomains.length > 0 && (
                  <>
                    <button
                      onClick={() => toggleSection("domainHealth")}
                      className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
                      style={{ color: "var(--color-default)" }}
                    >
                      {collapsedSections.domainHealth ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                      <h2 className="text-lg font-medium">Domain Health</h2>
                      <span className="text-sm" style={{ color: "var(--color-label)" }}>
                        ({processedDomains.length})
                      </span>
                    </button>
                    {!collapsedSections.domainHealth && (
                      <div className="health-domain-grid">
                        {processedDomains.map((domain) => (
                          <DomainHealthCard key={domain.name} domain={domain} />
                        ))}
                      </div>
                    )}
                  </>
                )
              )}
            </div>
          </div>

          {/* Right column: Issues, Agent Status */}
          <div className="health-right">
            {/* Issues */}
            <div className="health-issues">
              {isWorkloadsLoading ? (
                <>
                  <h2 className="text-lg font-medium mb-4" style={{ color: "var(--color-default)" }}>
                    Issues
                  </h2>
                  <SectionLoader count={4} className="health-issues-grid" />
                </>
              ) : (
                allIssues.length > 0 && (
                  <>
                    <button
                      onClick={() => toggleSection("workloadHealth")}
                      className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
                      style={{ color: "var(--color-default)" }}
                    >
                      {collapsedSections.workloadHealth ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                      <h2 className="text-lg font-medium">Issues</h2>
                      <span className="text-sm" style={{ color: "var(--color-label)" }}>
                        ({allIssues.length})
                      </span>
                    </button>
                    {!collapsedSections.workloadHealth && (
                      <>
                        <div className="health-issues-grid">
                          {allIssues.slice(0, visibleIssueCount).map((issue) => (
                            <HealthIssueCard
                              key={issue.id}
                              issue={issue}
                              isExpanded={expandedIssueId === issue.id}
                              onToggle={() => setExpandedIssueId(expandedIssueId === issue.id ? null : issue.id)}
                            />
                          ))}
                        </div>
                        {allIssues.length > 6 && (
                          <div className="flex justify-center gap-4 mt-3">
                            {visibleIssueCount > 6 && (
                              <button
                                onClick={() => setVisibleIssueCount(6)}
                                className="text-sm hover:underline"
                                style={{ color: "var(--color-link)" }}
                              >
                                Show less
                              </button>
                            )}
                            {visibleIssueCount < allIssues.length && (
                              <button
                                onClick={() => setVisibleIssueCount((prev) => Math.min(prev + 6, allIssues.length))}
                                className="text-sm hover:underline"
                                style={{ color: "var(--color-link)" }}
                              >
                                See more ({allIssues.length - visibleIssueCount} remaining)
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )
              )}
            </div>

            {/* Agent Status */}
            <div className="health-agents">
              {isAgentsLoading ? (
                <>
                  <h2 className="text-lg font-medium mb-4" style={{ color: "var(--color-default)" }}>
                    Agent Status
                  </h2>
                  <SectionLoader count={4} className="health-agents-grid" />
                </>
              ) : (
                sortedAgents.length > 0 && (
                  <>
                    <button
                      onClick={() => toggleSection("agentStatus")}
                      className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
                      style={{ color: "var(--color-default)" }}
                    >
                      {collapsedSections.agentStatus ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                      <h2 className="text-lg font-medium">Agent Status</h2>
                      <span className="text-sm" style={{ color: "var(--color-label)" }}>
                        ({sortedAgents.length})
                      </span>
                    </button>
                    {!collapsedSections.agentStatus && (
                      <div className="health-agents-grid">
                        {sortedAgents.map((agent) => (
                          <AgentHealthCard key={agent.name} agent={agent} info={agentInfoMap[agent.name]} />
                        ))}
                      </div>
                    )}
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
